import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { categories, collectionsByDate, monthNames, toKey, weekdayNames, type CategoryId } from '../../data/calendrierCollectes';
interface CollectionCalendarProps {
  month: Date;
  selected: Date;
  onMonthChange: (month: Date) => void;
  onSelect: (date: Date) => void;
}
function buildGrid(month: Date): (Date | null)[] {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const first = new Date(year, monthIndex, 1);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells: (Date | null)[] = Array.from({
    length: offset
  }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, monthIndex, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
export function CollectionCalendar({
  month,
  selected,
  onMonthChange,
  onSelect
}: CollectionCalendarProps) {
  const cells = buildGrid(month);
  const selectedKey = toKey(selected);
  const todayKey = toKey(new Date(2026, 8, 10));
  const legend = Object.values(categories);
  return <section aria-label="Collection calendar" className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-7" data-id="element-611" data-mp="100tlbcwqeunl">
      <div className="flex items-center justify-between" data-id="element-612" data-mp="w0asbfj3rijt">
        <button type="button" aria-label="Previous month" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-600 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-613" data-mp="vxojzdoe0z0j">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-614" data-mp="89x8udel2s0u" />
        </button>
        <h2 aria-live="polite" className="text-base font-bold tracking-[0.14em] text-ville-navy" data-id="element-615" data-mp="87u9v3vgdojz">
          {monthNames[month.getMonth()]} {month.getFullYear()}
        </h2>
        <button type="button" aria-label="Next month" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-600 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-616" data-mp="4sizllc9v0nz">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-617" data-mp="3i5bb0ky3cn4a" />
        </button>
      </div>

      <div className="mt-6 grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wide text-slate-400" data-id="element-618" data-mp="3mz71l2c0z7pf">
        {weekdayNames.map(day => <span key={day} data-id="element-619" data-mp="3d7jflgy8mivc">{day}</span>)}
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1" data-id="element-620" data-mp="3d8cddsf7xy1c">
        {cells.map((date, index) => {
        if (!date) return <span key={`empty-${index}`} aria-hidden="true" data-id="element-621" data-mp="2z8ti407iah6g" />;
        const key = toKey(date);
        const events: CategoryId[] = collectionsByDate[key] ?? [];
        const isSelected = key === selectedKey;
        const isToday = key === todayKey;
        return <button key={key} type="button" onClick={() => onSelect(date)} aria-pressed={isSelected} aria-label={`${date.getDate()} ${monthNames[date.getMonth()].toLowerCase()}${events.length ? `, ${events.length} collection(s)` : ', no collection'}`} className={`flex h-14 flex-col items-center justify-center gap-1 rounded-2xl border text-sm transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${isSelected ? 'border-ville-indigo bg-ville-indigo-soft font-bold text-ville-indigo' : 'border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50'}`} data-id="element-622" data-mp="2fn1i8r87ylyf" data-mpi={index}>
              <span className={isToday && !isSelected ? 'font-bold text-ville-navy underline underline-offset-4' : ''} data-id="element-623" data-mp="38bfh1974tmb1">
                {date.getDate()}
              </span>
              <span className="flex h-1.5 items-center gap-0.5" data-id="element-624" data-mp="3hcdcv47f2nij">
                {events.slice(0, 4).map(id => <span key={id} className={`h-1.5 w-1.5 rounded-full ${categories[id].dot}`} aria-hidden="true" data-id="element-625" data-mp="1fn7ooxneqlrs" />)}
              </span>
            </button>;
      })}
      </div>

      <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 border-t border-slate-100 pt-5" data-id="element-626" data-mp="1pchiqj1taeij">
        {legend.map(category => <li key={category.id} className="flex items-center gap-2 text-xs text-slate-500" data-id="element-627" data-mp="1techpgw9sft5">
            <span className={`h-2 w-2 rounded-full ${category.dot}`} aria-hidden="true" data-id="element-628" data-mp="wo3jjzyzlc5e" />
            {category.label}
          </li>)}
      </ul>
    </section>;
}